import posthog from 'posthog-js';
import { z } from 'zod';

import { getCookieValue } from 'shared/utils/getCookieValue';

import { ENVIRONMENTS, env } from './env';

/**
 * Cookie set by the AQA harness to force PostHog feature-flag values on non-production environments.
 * Name and value shape are a cross-team contract — do not change unilaterally.
 */
export const FEATURE_FLAG_OVERRIDES_COOKIE_NAME = 'bl_feature_flag_overrides';

const overridesSchema = z.object({ flags: z.record(z.string(), z.boolean()) });

/** One URL-decode step; `null` when decoding fails or makes no progress. */
const decodeUriComponentStep = (value: string) => {
  try {
    const decoded = decodeURIComponent(value);

    return decoded === value ? null : decoded;
  } catch {
    return null;
  }
};

/**
 * Parses the raw `bl_feature_flag_overrides` cookie value — URL-encoded JSON
 * `{"flags":{"<flag-key>":boolean}}` — into a flag-key → value record.
 * Returns `null` for anything malformed or empty (whole-or-nothing, matching
 * the API-side parser) so a broken cookie can never affect flag evaluation.
 */
export const parseFeatureFlagOverridesCookie = (rawValue: string): Record<string, boolean> | null => {
  let candidate: string | null = rawValue;

  // The contract is single-URL-encoded JSON; raw and double-encoded values
  // are tolerated to match the API-side parser's leniency.
  for (let depth = 0; depth < 3 && candidate !== null; depth += 1) {
    try {
      const result = overridesSchema.safeParse(JSON.parse(candidate));

      return result.success && Object.keys(result.data.flags).length > 0 ? result.data.flags : null;
    } catch {
      candidate = decodeUriComponentStep(candidate);
    }
  }

  return null;
};

/**
 * Reads the AQA override cookie and forces the contained feature-flag values
 * in the PostHog client, or clears previously forced values when the cookie
 * is absent or malformed. No-op in production. Must run after
 * `posthog.init()` — `overrideFeatureFlags` ignores calls on an uninitialized
 * instance.
 */
export const initFeatureFlagOverrides = (): void => {
  if (env.environment === ENVIRONMENTS.PRODUCTION) {
    return;
  }

  const rawValue = getCookieValue(FEATURE_FLAG_OVERRIDES_COOKIE_NAME);
  const overrides = rawValue === null ? null : parseFeatureFlagOverridesCookie(rawValue);

  if (overrides) {
    // suppressWarning silences the SDK's "Overriding feature flags!" console.warn
    // fired on flag reads while overrides are active — cosmetic only, and avoids
    // per-page-load noise in AQA runs.
    posthog.featureFlags.overrideFeatureFlags({ flags: overrides, suppressWarning: true });
  } else {
    // The SDK persists overrides in localStorage across page loads — the
    // explicit clear stops a stale override from an earlier session from
    // applying once the cookie is gone. Clearing when none are set is a no-op.
    posthog.featureFlags.overrideFeatureFlags(false);
  }
};
